import { Tooltip } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { PermissionExpandableSection } from '@affine/core/modules/permissions/views/permission-create-modal-core';
import { ShareInfoService } from '@affine/core/modules/share-doc';
import type { WorkspaceMetadata } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import type { Store } from '@blocksuite/affine/store';
import { CollaborationIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { type ComponentProps, forwardRef, type Ref, useEffect } from 'react';

import { DocPermissionSections } from './doc-permission-sections';
import * as styles from './index.css';
import { InviteMemberEditor } from './invite-member-editor/invite-member-editor';
import { ShareExport } from './share-export';
import { SharePage } from './share-page';
import { useDocShareRestrictions } from './use-doc-share-restrictions';

export interface ShareMenuProps {
  workspaceMetadata: WorkspaceMetadata;
  currentPage: Store;
  onEnableAffineCloud: () => void;
  folderId?: string;
  hideExport?: boolean;
  hideGuests?: boolean;
  hideLinkSharing?: boolean;
}

export const ShareMenuContent = (props: ShareMenuProps) => {
  const t = useI18n();
  const { hideExport, hideGuests, hideLinkSharing } = props;
  const { isReady, privateFolderDocShareBlocked } = useDocShareRestrictions();

  if (props.workspaceMetadata.flavour === 'local') {
    return (
      <div className={styles.containerStyle}>
        {hideLinkSharing ? null : <SharePage {...props} />}
      </div>
    );
  }

  return (
    <div className={styles.containerStyle}>
      <DocPermissionSections
        disabled={!isReady || privateFolderDocShareBlocked}
      />
      {hideGuests ? null : (
        <PermissionExpandableSection
          title={t['com.streamlain.share-menu.doc-permissions.guests']()}
          defaultCollapsed
        >
          <InviteMemberEditor
            onClickCancel={() => undefined}
            folderId={props.folderId}
            guestOnly
            disabled={!isReady || privateFolderDocShareBlocked}
          />
        </PermissionExpandableSection>
      )}
      {hideLinkSharing ? null : (
        <PermissionExpandableSection
          title={t['com.streamlain.share-menu.doc-permissions.link']()}
          defaultCollapsed
        >
          <SharePage {...props} hideMemberControls />
        </PermissionExpandableSection>
      )}
      {!hideExport && !BUILD_CONFIG.isMobileEdition ? (
        <PermissionExpandableSection title={t['Export']()} defaultCollapsed>
          <ShareExport />
        </PermissionExpandableSection>
      ) : null}
    </div>
  );
};

export const DefaultShareButton = forwardRef(function DefaultShareButton(
  props: ComponentProps<typeof Button>,
  ref: Ref<HTMLButtonElement>
) {
  const t = useI18n();
  const shareInfoService = useService(ShareInfoService);
  const shared = useLiveData(shareInfoService.shareInfo.isShared$);

  useEffect(() => {
    shareInfoService.shareInfo.revalidate();
  }, [shareInfoService]);

  return (
    <Tooltip
      content={
        shared
          ? t['com.streamlain.share-menu.option.link.readonly.description']()
          : t['com.streamlain.share-menu.option.link.no-access.description']()
      }
    >
      <Button {...props} ref={ref} className={styles.button} variant="primary">
        <div className={styles.buttonContainer}>
          <CollaborationIcon fontSize={16} />
          {t['com.streamlain.share-menu.shareButton']()}
        </div>
      </Button>
    </Tooltip>
  );
});
